import * as Sentry from '@sentry/browser'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import resolveConfig from 'tailwindcss/resolveConfig'

import tailwindConfig from '../../../tailwind.config.js'
import { useQueryHistoryStore } from '../../store'
import { Column } from '../queryResultTable/QueryResultTable'
import { DQLEditorContext } from './context'
import { DQLEditorProps } from './DQLEditor'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Improperly typed
const tailwind = resolveConfig(tailwindConfig)

type Props = {
  children: React.ReactNode
} & DQLEditorProps

export default function DQLEditorProvider({
  children,
  accessToken,
  refreshToken,
  executeQuery,
  initialQuery,
  logDataAccessedEvent,
  ...props
}: Props) {
  // Use a separate internal state for setting the query so we can control when the query is executed
  // This maintains backwards compatibility with the old setQuery function
  const [query, setQueryState] = useState(initialQuery)
  const [shouldExecute, setShouldExecute] = useState(true)

  // By default, execute the query. If execute is false, the query will not be executed
  const setQuery = (newQuery: string, execute = true) => {
    setShouldExecute(execute)
    setQueryState(newQuery)
  }

  const [sideBarOpen, setSideBarOpen] = useState(true)
  const [historySideBarOpen, setHistorySideBarOpen] = useState(true)

  const [showLatestTour, setShowLatestTour] = useState(false)

  const [activeView, setActiveView] = useState<'collections' | 'editor'>(
    'collections',
  )

  const [activeRow, setActiveRow] = useState<Column | undefined>(undefined)

  const [refetchInterval, setRefetchInterval] = useState<number | false>(false)

  const { addQuery } = useQueryHistoryStore()

  /**
   * A naive ref to track the queries which have been audited already this
   * session
   */
  const reportedRef = useRef<Record<string, boolean>>({})

  const {
    data,
    isLoading,
    error: serviceError,
    fetchStatus,
    refetch,
  } = useQuery(['dql', query], () => executeQuery(query || ''), {
    enabled: !!query && !!accessToken && shouldExecute,
    refetchInterval,
    // Errors should only be thrown for more serious failures, e.g. 5xx errors,
    // from the HTTP API. If we want to report on 4xx errors, or other user-facing
    // errors, we should consider another approach.
    onError: async (error: unknown) => {
      const shouldRetry = isAxiosError(error) && error.response?.status === 401
      if (shouldRetry && refreshToken) {
        await refreshToken()
        refetch()
      } else {
        Sentry.withScope((scope) => {
          // NOTE: I am not embedding the query in the tag in case it contains sensitive information
          scope.setTag(
            'dql-editor-query-error',
            'HTTP API returned an error for a DQL query',
          )
          Sentry.captureException(error)
        })
      }
    },

    // Adds the query to the history on success
    onSuccess: () => {
      addQuery({
        appId: props?.appId || '',
        query: query || '',
        createdAt: new Date().getTime(),
        lastUsed: new Date().getTime(),
        isFavorite: false,
        activeView,
      })
    },
  })

  /**
   * An effect to set the query to the initial query whenever it changes
   */
  useEffect(() => {
    if (initialQuery) {
      // Make sure to execute the initial query
      setQuery(initialQuery, true)
    }
  }, [initialQuery, props?.appCollections])

  /**
   * An effect to log an access event whenever the query changes
   */
  useEffect(() => {
    if (!query || !logDataAccessedEvent || !shouldExecute) {
      return
    }

    const isUnreported = !reportedRef.current[query]
    if (isUnreported) {
      reportedRef.current[query] = true
      // Note: technically this query _can_ be a mutation... But, without a parser or
      // some brittle regex, at least when considering better complex query support down
      // the road, we can't know which.
      logDataAccessedEvent({
        description: 'A user executed a DQL query',
        query,
      })
    }

    // Reset shouldExecute for next time
    setShouldExecute(true)
  }, [query, logDataAccessedEvent, shouldExecute])

  const queryError = data?.error
  const errorMessage = useMemo(() => {
    if (queryError?.description) {
      return queryError.description
    }

    if (serviceError instanceof Error) {
      return serviceError.message
    } else if (!!serviceError) {
      // NOTE: at this point we would have already reported the error to Sentry,
      // so we don't need to do it again.
      return 'An unknown error occurred'
    }
  }, [serviceError, queryError])

  return (
    <DQLEditorContext.Provider
      value={{
        result: data,
        // Loading state gets stuck on true if the query is not enabled, a bit unintuitive.
        isLoading: isLoading && fetchStatus !== 'idle',
        errorMessage,
        accessToken,
        query,
        setQuery,
        refetch,
        refetchInterval,
        setRefetchInterval,
        tailwind,
        sideBarOpen,
        setSideBarOpen,
        historySideBarOpen,
        setHistorySideBarOpen,
        showLatestTour,
        setShowLatestTour,
        logDataAccessedEvent,
        activeView,
        setActiveView,
        activeRow,
        setActiveRow,
        ...props,
      }}
    >
      {children}
    </DQLEditorContext.Provider>
  )
}
